import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import RadioItem from 'src/components/primitives/RadioGroup/RadioItem';
import {
	RadioGroupSize,
	BaseRadioItemProps,
	RadioGroupVariant,
} from 'src/components/primitives/RadioGroup/types';
import { cm } from 'src/utils/classNames';
import { typedMemo } from 'src/utils/react';

interface Props<T extends string> extends Pick<
	RadixRadioGroup.RadioGroupProps,
	| 'asChild'
	| 'defaultValue'
	| 'disabled'
	| 'name'
	| 'required'
	| 'orientation'
	| 'dir'
	| 'loop'
	| 'className'
> {
	value?: T;
	onValueChange?: (value: T) => void;
	variant?: RadioGroupVariant;
	/**
	 * Only affects text size for `default`.
	 * Affects spacing and text size for `card`.
	 * `lg` only has an effect on `card` variant.
	 */
	size: RadioGroupSize;
	items: BaseRadioItemProps<T>[];
	dataTestId?: string;
	itemTestIdPrefix?: string;
}

function RadioGroup<T extends string>({
	variant = 'standard',
	size,
	items,
	className,
	orientation,
	disabled: groupDisabled,
	dataTestId,
	itemTestIdPrefix,
	...rest
}: Props<T>) {
	return (
		<RadixRadioGroup.Root
			className={cm(
				'group flex gap-xs',
				orientation === 'vertical' && 'flex-col',
				className
			)}
			orientation={orientation}
			disabled={groupDisabled}
			data-testid={dataTestId}
			{...rest}
		>
			{items.map(({ disabled: itemDisabled, ...itemRest }) => {
				return (
					<RadioItem
						variant={variant}
						key={itemRest.id}
						size={size}
						disabled={groupDisabled || itemDisabled}
						dataTestId={
							itemTestIdPrefix
								? `${itemTestIdPrefix}-${itemRest.value}`
								: undefined
						}
						{...itemRest}
					/>
				);
			})}
		</RadixRadioGroup.Root>
	);
}

export default {
	/**
	 * Default configuration of the `RadioGroup` component.
	 * You can build your own with `Root` and `Item` if you need more customization.
	 */
	Default: typedMemo(RadioGroup),
	Root: typedMemo(RadixRadioGroup.Root),
	Item: typedMemo(RadioItem),
};
